import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment
} from "@mui/material";
import {
  CheckOutlined,
  EditOutlined,
  OpenInNewOutlined
} from "@mui/icons-material";
import { CommentDto, Broker, LegalLifecycle } from "../../../../../../api/Api";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  agreementReview: string;
  agreementReviewAssignee: string;
  ndaReview: string;
  ndaReviewAssignee: string;
  comments: CommentDto[];
  clearContractCase: string;
  signOffNote: string;
};

export const Legal: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(specificBroker, "legal");

  const [formData, setFormData] = useState<FormData>({
    agreementReview: specificBroker?.legalLifecycle?.agreementReview || "",
    agreementReviewAssignee:
      specificBroker?.legalLifecycle?.agreementReviewAssignee || "",
    ndaReview: specificBroker?.legalLifecycle?.ndaReview || "",
    ndaReviewAssignee: specificBroker?.legalLifecycle?.ndaReviewAssignee || "",
    comments: specificBroker?.legalLifecycle?.comments || [],
    clearContractCase: specificBroker?.legalLifecycle?.clearContractCase || "",
    signOffNote: specificBroker?.legalLifecycle?.signOffNote || ""
  });

  const ndaRequired =
    specificBroker.marketAccessLifecycle?.ndaReview !== undefined &&
    specificBroker.marketAccessLifecycle.ndaReview !== "not_needed";

  const [isClearContractCaseEditable, setIsClearContractCaseEditable] =
    useState(false);

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateBroker = useUpdateBroker({ onSuccess: handleClose });
  const commentUpdate = useUpdateBroker();

  const buildBroker = (legalLifecycle: LegalLifecycle): Broker => ({
    ...specificBroker,
    legalLifecycle: {
      ...specificBroker.legalLifecycle,
      ...legalLifecycle
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...formData, comments })
  });

  const legalAssignees = useBrokerAssignees("legal");
  const clearContractCaseUrl = buildValidUrl(formData.clearContractCase);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleClearContractCaseEditing = () => {
    setIsClearContractCaseEditable((prev) => !prev);
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificBroker.progress?.legal,
    canSetInProgress: ownsLifecycle(roles, "legal"),
    buildEntity: (legalLifecycle, progress) => ({
      ...buildBroker(legalLifecycle),
      progress: { ...specificBroker.progress, legal: progress }
    }),
    mutate: updateBroker.mutate
  });

  const open = location.pathname.includes("legal");

  const canComplete =
    formData.agreementReview && (ndaRequired ? formData.ndaReview : true);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Legal"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Agreement review</InputLabel>
            <Select
              label="Agreement review"
              value={formData.agreementReview}
              onChange={(e) => handleChange("agreementReview", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={legalAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              legalAssignees.data?.find(
                (a) => a.email === formData.agreementReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("agreementReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agreement review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {ndaRequired && (
          <Box display="flex" gap={2}>
            <FormControl size="medium" required sx={{ flex: 1 }}>
              <InputLabel>NDA Review</InputLabel>
              <Select
                label="NDA Review"
                value={formData.ndaReview}
                onChange={(e) => handleChange("ndaReview", e.target.value)}
              >
                <MenuItem value="initiated">Initiated</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={legalAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                legalAssignees.data?.find(
                  (a) => a.email === formData.ndaReviewAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("ndaReviewAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="NDA review assignee"
                  size="medium"
                />
              )}
            />
          </Box>
        )}

        <TextField
          label="Clear Contract case link"
          value={formData.clearContractCase}
          onChange={(e) => handleChange("clearContractCase", e.target.value)}
          fullWidth
          disabled={!isClearContractCaseEditable}
          slotProps={{
            input: {
              startAdornment: clearContractCaseUrl ? (
                <InputAdornment position="start">
                  <Tooltip title="Open clear contract case link">
                    <IconButton
                      onClick={() =>
                        window.open(
                          clearContractCaseUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      edge="start"
                      color="primary"
                    >
                      <OpenInNewOutlined />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      isClearContractCaseEditable
                        ? "Lock clear contract case"
                        : "Unlock clear contract case for editing"
                    }
                  >
                    <IconButton
                      onClick={toggleClearContractCaseEditing}
                      edge="end"
                      color={
                        isClearContractCaseEditable ? "secondary" : "primary"
                      }
                    >
                      {isClearContractCaseEditable ? (
                        <CheckOutlined />
                      ) : (
                        <EditOutlined />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }
          }}
          autoFocus={isClearContractCaseEditable}
          helperText={
            isClearContractCaseEditable
              ? "Editing enabled until you lock it again or save the dialog."
              : clearContractCaseUrl
                ? "Disabled for editing. Use the open icon to follow the link."
                : "Locked to reduce accidental changes."
          }
        />

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};

const buildValidUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    const url = new URL(trimmedValue);

    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};
